import styled, { css } from 'styled-components'
import { backgroundOrange } from '@/theme/colors'
import { contentShell, shellGutter } from '@/theme/layout'
import { contentWidth, media } from '@/theme/tokens'

/** What the shell leaves a page between its gutters. The cap only binds past `contentWidth`, where the gutters are the wide ones. */
const shellContentWidth = `calc(${contentWidth} - 2 * ${shellGutter.wide})`

/**
 * A row inside a `$fluid` column held to the shell's width, so it sits under the
 * chrome's edges. The width too: auto margins alone would shrink the row to its
 * content where the column is a flex one.
 */
export const onShellWidth = css`
	width: 100%;
	max-width: ${shellContentWidth};
	margin-left: auto;
	margin-right: auto;
`

/**
 * The page's scrolling ground: the warm cream filling the layout's content
 * area, scrolling as one so the wheel works across the full viewport width.
 * Whatever must sit outside the column — a hidden file input, a full-bleed
 * banner, the page's modals — mounts here beside the PageColumn.
 */
export const PageScroll = styled.div`
	flex: 1;
	min-height: 0;
	width: 100%;
	display: flex;
	flex-direction: column;
	background-color: ${backgroundOrange};
	overflow-y: auto;
`

/**
 * The chrome's row on the shared shell: the navbar and the public site header
 * each sit inside a full-bleed band that owns its own
 * vertical padding and border, so they take only the column and its gutters,
 * never the page column's air above and below.
 */
export const ShellRow = styled.div`
	${contentShell}
`

/** The page's first row: heading left, actions right; on a phone the actions drop under the heading and fill the row. */
export const PageHeaderRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px 16px;
	flex-wrap: wrap;
	margin-bottom: 24px;

	${media.phone} {
		margin-bottom: 16px;
		gap: 8px 12px;
	}
`

/**
 * The centered content column on the shared shell (contentShell: contentWidth
 * with the chrome's 36px gutters, 18px once the bento collapses), with 36px of
 * air above and 64px below — 24/48 on the collapsed grid, 16/40 on phones. The
 * navbar and every page share this geometry, so their edges
 * line up.
 *
 * `$fluid` lifts the width cap for a workspace page whose panes can use every
 * pixel (the itinerary: calendar, day, map): full width on the same gutters.
 * The chrome stays on `contentWidth`, so past it the page runs wider than the
 * bars above it — by design, as the full-bleed trip map already does. Its
 * heading row stays on the shell, so the title and the actions still sit under
 * the chrome's edges.
 */
export const PageColumn = styled.div<{ $fluid?: boolean }>`
	${contentShell}
	${({ $fluid }) =>
		$fluid &&
		css`
			max-width: none;

			> ${PageHeaderRow} {
				${onShellWidth}
			}
		`}
	width: 100%;
	flex-shrink: 0;
	padding-top: 36px;
	padding-bottom: 64px;

	${media.bentoCollapse} {
		padding-top: 24px;
		padding-bottom: 48px;
	}

	${media.phone} {
		padding-top: 16px;
		padding-bottom: 40px;
	}
`

/**
 * A `$fluid` column that also fills the scrolling ground above the tabletWide
 * breakpoint, so a workspace card under the heading can take every pixel the
 * viewport has left, with a short margin under it instead of the column's
 * usual 64px. Below that breakpoint the card stacks and grows with its content.
 */
export const FillPageColumn = styled(PageColumn).attrs({ $fluid: true })`
	${media.aboveTabletWide} {
		flex: 1 0 auto;
		display: flex;
		flex-direction: column;
		padding-bottom: 24px;
	}
`

/**
 * The ground for a page whose own pane scrolls — the Chat's stream — rather than the
 * page: it fills the content area and never scrolls itself.
 */
export const PaneScroll = styled(PageScroll)`
	overflow: hidden;
`

/**
 * The shell's column filling a `PaneScroll` at every width, its heading row on top and
 * the pane taking what's left, with a short margin under it instead of the column's
 * usual air: the pane, not the page, is what scrolls.
 */
export const PanePageColumn = styled(PageColumn)`
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
	padding-bottom: 24px;

	${media.bentoCollapse} {
		padding-bottom: 16px;
	}

	${media.phone} {
		padding-bottom: 12px;
	}
`

export const PageActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	flex-wrap: wrap;
	margin-left: auto;

	/* Its own line under the heading, each action stretched across it. */
	${media.phone} {
		gap: 8px;
		flex-basis: 100%;
		margin-left: 0;

		> * {
			flex: 1;
		}
	}
`
